/**
 * BobbyProductGallery - Clean, lightweight product gallery
 * No dependencies, mobile-first approach with touch/swipe support
 */
class BobbyProductGallery {
    constructor(container) {
        this.container = container;
        this.galleryContainer = container.querySelector('.bobby-gallery-container');
        this.slides = container.querySelectorAll('.bobby-gallery-slide');
        this.dots = container.querySelectorAll('.bobby-dot');
        this.thumbs = container.querySelectorAll('.bobby-thumb');
        this.currentSlide = 0;
        this.isMobile = window.innerWidth < 750;

        // Debounced resize handler
        this.resizeTimeout = null;

        this.init();
    }

    init() {
        if (this.slides.length <= 1) return;

        // Add click handlers to dots
        this.dots.forEach((dot, index) => {
            dot.addEventListener('click', (e) => {
                e.preventDefault();
                this.goToSlide(index);
            });
        });

        // Add click handlers to thumbnails (if they exist)
        this.thumbs.forEach((thumb, index) => {
            thumb.addEventListener('click', (e) => {
                e.preventDefault();
                this.goToSlide(index);
            });
        });

        // Add keyboard navigation
        this.addKeyboardNavigation();

        // Add touch/swipe support
        this.addTouchSupport();

        // Add scroll listener for mobile
        if (this.isMobile) {
            this.addScrollSupport();
        }

        // Handle resize
        window.addEventListener('resize', () => this.handleResize());

        // Handle lazy loading
        this.handleLazyLoading();

        // Initialize intersection observer for performance
        this.initIntersectionObserver();
        this.checkInitialVariant();
        this.listenVariantChange();
    }

    goToSlide(index, smooth = true) {
        if (index === this.currentSlide || index < 0 || index >= this.slides.length) {
            return;
        }

        const previousSlide = this.currentSlide;
        this.currentSlide = index;

        if (this.isMobile) {
            // For mobile, scroll to the slide with precise positioning
            const slideWidth = this.galleryContainer.offsetWidth;
            const targetScrollLeft = slideWidth * index;

            if (smooth) {
                // Use smooth scroll for better visual effect
                this.galleryContainer.scrollTo({
                    left: targetScrollLeft,
                    behavior: 'smooth'
                });
            } else {
                // Instant positioning without animation
                this.galleryContainer.scrollLeft = targetScrollLeft;
            }
        } else {
            // For desktop, toggle active classes
            this.slides[previousSlide].classList.remove('bobby-active');
            this.slides[this.currentSlide].classList.add('bobby-active');
        }

        // Update dots and thumbnails
        this.updateNavigation();

        // Load media if needed
        this.loadSlideMedia(this.currentSlide);

        // Announce to screen readers
        this.announceSlideChange();
    }

    updateNavigation() {
        // Update dots
        this.dots.forEach((dot, index) => {
            dot.classList.toggle('bobby-active', index === this.currentSlide);
            dot.setAttribute('aria-pressed', index === this.currentSlide);
        });

        // Update thumbnails only if they exist
        if (this.thumbs.length > 0) {
            this.thumbs.forEach((thumb, index) => {
                thumb.classList.toggle('bobby-active', index === this.currentSlide);
                thumb.setAttribute('aria-pressed', index === this.currentSlide);
            });
        }
    }

    addKeyboardNavigation() {
        this.container.addEventListener('keydown', (e) => {
            if (e.target.classList.contains('bobby-dot') || e.target.classList.contains('bobby-thumb')) {
                switch (e.key) {
                    case 'ArrowLeft':
                        e.preventDefault();
                        this.goToSlide(this.currentSlide - 1);
                        break;
                    case 'ArrowRight':
                        e.preventDefault();
                        this.goToSlide(this.currentSlide + 1);
                        break;
                    case 'Home':
                        e.preventDefault();
                        this.goToSlide(0);
                        break;
                    case 'End':
                        e.preventDefault();
                        this.goToSlide(this.slides.length - 1);
                        break;
                }
            }
        });
    }

    addTouchSupport() {
        let startX = 0;
        let startY = 0;
        let endX = 0;
        let endY = 0;
        let isDragging = false;

        // Mouse events for desktop
        this.galleryContainer.addEventListener('mousedown', (e) => {
            if (this.isMobile) return;
            startX = e.clientX;
            startY = e.clientY;
            isDragging = true;
            this.galleryContainer.style.cursor = 'grabbing';
            e.preventDefault();
        });

        this.galleryContainer.addEventListener('mousemove', (e) => {
            if (!isDragging || this.isMobile) return;
            e.preventDefault();
        });

        this.galleryContainer.addEventListener('mouseup', (e) => {
            if (!isDragging || this.isMobile) return;
            endX = e.clientX;
            endY = e.clientY;
            this.handleSwipe(startX, startY, endX, endY);
            isDragging = false;
            this.galleryContainer.style.cursor = 'grab';
        });

        this.galleryContainer.addEventListener('mouseleave', () => {
            if (isDragging) {
                isDragging = false;
                this.galleryContainer.style.cursor = 'grab';
            }
        });

        // Touch events
        this.galleryContainer.addEventListener('touchstart', (e) => {
            const touch = e.touches[0];
            startX = touch.clientX;
            startY = touch.clientY;
        }, { passive: true });

        this.galleryContainer.addEventListener('touchend', (e) => {
            const touch = e.changedTouches[0];
            endX = touch.clientX;
            endY = touch.clientY;
            this.handleSwipe(startX, startY, endX, endY);
        }, { passive: true });
    }

    addScrollSupport() {
        let scrollTimeout;

        this.galleryContainer.addEventListener('scroll', () => {
            clearTimeout(scrollTimeout);

            scrollTimeout = setTimeout(() => {
                const scrollLeft = this.galleryContainer.scrollLeft;
                const slideWidth = this.galleryContainer.offsetWidth;
                const newIndex = Math.round(scrollLeft / slideWidth);

                if (newIndex !== this.currentSlide && newIndex >= 0 && newIndex < this.slides.length) {
                    this.currentSlide = newIndex;
                    this.updateNavigation();
                    this.loadSlideMedia(this.currentSlide);
                    this.announceSlideChange();
                }
            }, 100);
        }, { passive: true });
    }

    handleSwipe(startX, startY, endX, endY) {
        const threshold = 30; // Reduced threshold for more responsive touch
        const diffX = startX - endX;
        const diffY = startY - endY;

        // Only handle horizontal swipes (ignore if vertical swipe is dominant)
        if (Math.abs(diffX) > Math.abs(diffY) && Math.abs(diffX) > threshold) {
            if (diffX > 0) {
                // Swipe left - next slide (only one slide)
                this.goToSlide(this.currentSlide + 1);
            } else {
                // Swipe right - previous slide (only one slide)
                this.goToSlide(this.currentSlide - 1);
            }
        }
    }

    loadSlideMedia(index) {
        const slide = this.slides[index];
        if (!slide) return;

        // Load lazy images
        const lazyImage = slide.querySelector('img[loading="lazy"]');
        if (lazyImage && !lazyImage.classList.contains('bobby-loaded')) {
            lazyImage.addEventListener('load', () => {
                lazyImage.classList.add('bobby-loaded');
            }, { once: true });
        }

        // Preload next slide media
        if (index + 1 < this.slides.length) {
            const nextSlide = this.slides[index + 1];
            const nextImage = nextSlide.querySelector('img[loading="lazy"]');
            if (nextImage && !nextImage.complete) {
                nextImage.loading = 'eager';
            }
        }
    }

    handleResize() {
        clearTimeout(this.resizeTimeout);
        this.resizeTimeout = setTimeout(() => {
            const wasMobile = this.isMobile;
            this.isMobile = window.innerWidth < 750;

            // If switching between mobile and desktop, reinitialize
            if (wasMobile !== this.isMobile) {
                this.goToSlide(this.currentSlide, false);
            }
        }, 250);
    }

    announceSlideChange() {
        // Create or update live region for screen readers
        let liveRegion = this.container.querySelector('.bobby-sr-live-region');
        if (!liveRegion) {
            liveRegion = document.createElement('div');
            liveRegion.className = 'bobby-sr-live-region bobby-sr-only';
            liveRegion.setAttribute('aria-live', 'polite');
            liveRegion.setAttribute('aria-atomic', 'true');
            this.container.appendChild(liveRegion);
        }

        const currentSlideElement = this.slides[this.currentSlide];
        const mediaType = this.getMediaType(currentSlideElement);
        const message = `${mediaType} ${this.currentSlide + 1} of ${this.slides.length}`;

        liveRegion.textContent = message;
    }

    getMediaType(slide) {
        if (slide.querySelector('img')) return 'Image';
        if (slide.querySelector('video')) return 'Video';
        if (slide.querySelector('iframe')) return 'Video';
        if (slide.querySelector('model-viewer')) return '3D Model';
        return 'Media';
    }

    handleLazyLoading() {
        // Mark initial images as loaded if they're already loaded
        this.slides.forEach(slide => {
            const img = slide.querySelector('img');
            if (img && img.complete) {
                img.classList.add('bobby-loaded');
            }
        });
    }

    initIntersectionObserver() {
        if ('IntersectionObserver' in window) {
            const observer = new IntersectionObserver((entries) => {
                entries.forEach(entry => {
                    if (entry.isIntersecting) {
                        const img = entry.target;
                        if (img.loading === 'lazy') {
                            img.loading = 'eager';
                        }
                    }
                });
            }, {
                rootMargin: '50px'
            });

            this.slides.forEach(slide => {
                const img = slide.querySelector('img[loading="lazy"]');
                if (img) {
                    observer.observe(img);
                }
            });
        }
    }

    listenVariantChange() {
        // Listen to variant:change event
        document.addEventListener('variant:change', (e) => {
            if (!e.detail || !e.detail.variant) return;
            this.switchToVariant(e.detail.variant.id.toString());
        });

        // Fallback: listen to form select change
        const variantSelector = document.querySelector('[name="id"]');
        if (variantSelector) {
            variantSelector.addEventListener('change', (e) => {
                console.log('📝 Intercepted form change:', e.target.value);
                this.switchToVariant(e.target.value);
            });
        }
    }

    checkInitialVariant() {
        const variantSelector = document.querySelector('[name="id"]');
        if (variantSelector && variantSelector.value) {
            console.log('🎯 Initial variant detected:', variantSelector.value);

            // Small delay to ensure DOM is ready
            setTimeout(() => {
                this.switchToVariant(variantSelector.value);
            }, 100);
        }
    }

    switchToVariant(variantId) {
        console.log('🔄 Switching to variant:', variantId);

        const targetSlide = Array.from(this.slides).find(slide => {
            return slide.dataset.bobbyVariantId === variantId;
        });

        if (targetSlide) {
            const index = Array.from(this.slides).indexOf(targetSlide);
            console.log('✅ Found slide at index:', index);
            this.goToSlide(index, true);
        } else {
            console.log('❌ No slide for variant', variantId);
        }
    }

    // Public methods for external control
    next() {
        this.goToSlide(this.currentSlide + 1);
    }

    prev() {
        this.goToSlide(this.currentSlide - 1);
    }

    getCurrentSlide() {
        return this.currentSlide;
    }

    getTotalSlides() {
        return this.slides.length;
    }

    destroy() {
        // Clean up event listeners
        window.removeEventListener('resize', this.handleResize);

        // Remove live region
        const liveRegion = this.container.querySelector('.bobby-sr-live-region');
        if (liveRegion) {
            liveRegion.remove();
        }
    }
}

// Auto-initialize when DOM is ready
if (typeof window !== 'undefined') {
    document.addEventListener('DOMContentLoaded', () => {
        const galleries = document.querySelectorAll('.bobby-product-gallery:not([data-bobby-initialized])');
        galleries.forEach(gallery => {
            new BobbyProductGallery(gallery);
            gallery.setAttribute('data-bobby-initialized', 'true');
        });
    });
}

// Export for module usage
if (typeof module !== 'undefined' && module.exports) {
    module.exports = BobbyProductGallery;
}

// === DEBUG: Variant Change Detection ===
document.addEventListener('DOMContentLoaded', () => {
    console.log('=== GALLERY DEBUG INITIALIZED ===');

    // 1. Log product info
    const productForm = document.querySelector('form[action*="/cart/add"]');
    if (productForm) {
        const productId = productForm.querySelector('[name="id"]')?.value;
        console.log('Product ID:', productId);
    }

    // 2. Listen to variant changes (Shopify standard event)
    document.addEventListener('variant:change', (e) => {
        console.log('🔄 VARIANT CHANGE DETECTED:', e.detail);
        if (e.detail && e.detail.variant) {
            console.log('New Variant ID:', e.detail.variant.id);
            console.log('Featured Media ID:', e.detail.variant.featured_media?.id);
            console.log('Featured Image:', e.detail.variant.featured_image?.src);
        }
    });

    // 3. Listen to form changes (fallback)
    const variantSelector = document.querySelector('[name="id"]');
    if (variantSelector) {
        variantSelector.addEventListener('change', (e) => {
            console.log('📝 FORM CHANGE:', e.target.value);
        });
    }

    // 4. Log all gallery slides with their data
    const gallery = document.querySelector('.bobby-product-gallery');
    if (gallery) {
        const slides = gallery.querySelectorAll('.bobby-gallery-slide');
        console.log('Gallery Slides:', slides.length);
        slides.forEach((slide, i) => {
            const img = slide.querySelector('img');
            console.log(`Slide ${i}:`, {
                mediaId: slide.dataset.bobbyMediaId,
                imgSrc: img?.src,
                variantId: slide.dataset.bobbyVariantId || 'NO VARIANT ID'
            });
        });
    }
});